import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  TextField,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  useParams,
  useLocation,
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";
import { API_URL } from "../config";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ImageIcon from "@mui/icons-material/Image";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

/**
 * StudentAssignmentPage
 * ----------------------------
 */

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const PALETTE = {
  ink: "#1B2333",
  inkSoft: "#5B6478",
  canvas: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E5E8F0",
  primary: "#3A4DE0",
  primaryDark: "#2F3FC0",
  primarySoft: "#EEF0FD",
  amber: "#C97F1B",
  amberSoft: "#FBF1E1",
  success: "#1F9D74",
  successSoft: "#E7F6EF",
  danger: "#D6483F",
  dangerSoft: "#FBEAE8",
};

const FONT = {
  display: "'Sora', 'Segoe UI', sans-serif",
  body: "'Inter', 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
};
// -------------------------------------------

const MotionButton = motion(Button);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatBytes = (bytes) => {
  if (!bytes) return "0 MB";
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const resolvePdfUrl = (url) => {
  if (!url) return "";
  const clean = url.split("?")[0];
  return /\.pdf$/i.test(clean) ? url : `${url}.pdf`;
};

const wordCount = (text) =>
  (text || "").trim().split(/\s+/).filter(Boolean).length;

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
const SectionLabel = ({ icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
    <Box sx={{ color: PALETTE.inkSoft, display: "flex" }}>{icon}</Box>
    <Typography
      sx={{
        fontFamily: FONT.mono,
        fontSize: 11.5,
        fontWeight: 600,
        color: PALETTE.inkSoft,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {text}
    </Typography>
  </Box>
);

const ProgressRing = ({ value, total }) => {
  const size = 52;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? value / total : 0;
  const offset = circumference * (1 - pct);

  return (
    <Box
      sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={PALETTE.border}
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={
            value === total && total > 0 ? PALETTE.success : PALETTE.primary
          }
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT.mono,
            fontSize: 11,
            fontWeight: 700,
            color: PALETTE.ink,
          }}
        >
          {value}/{total}
        </Typography>
      </Box>
    </Box>
  );
};

const ImageLightbox = ({ open, onClose, src, label }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: { borderRadius: "16px", overflow: "hidden", bgcolor: "#11141C" },
    }}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <Box sx={{ position: "relative", p: 2 }}>
        <IconButton
          onClick={onClose}
          aria-label="Close preview"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.1)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {label && (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontFamily: FONT.body,
              fontSize: 13,
              mb: 1,
            }}
          >
            {label}
          </Typography>
        )}
        {src && (
          <Box
            component="img"
            src={src}
            alt={label || "Preview"}
            sx={{
              width: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              borderRadius: "8px",
              display: "block",
            }}
          />
        )}
      </Box>
    </motion.div>
  </Dialog>
);

const PdfPreviewPanel = ({ url, label }) => {
  const [loaded, setLoaded] = useState(false);
  const resolved = resolvePdfUrl(url);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <SectionLabel
          icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
          text={label}
        />
        <Button
          size="small"
          href={resolved}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
          sx={{
            textTransform: "none",
            fontSize: 12.5,
            color: PALETTE.primary,
            fontWeight: 600,
          }}
        >
          Open full screen
        </Button>
      </Box>

      <Box
        sx={{
          position: "relative",
          borderRadius: "12px",
          overflow: "hidden",
          border: `1px solid ${PALETTE.border}`,
        }}
      >
        {!loaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: PALETTE.canvas,
              zIndex: 1,
            }}
          >
            <CircularProgress size={22} sx={{ color: PALETTE.primary }} />
          </Box>
        )}
        <Box
          component="iframe"
          src={resolved}
          title={label}
          onLoad={() => setLoaded(true)}
          sx={{
            width: "100%",
            height: 380,
            border: "none",
            display: "block",
            bgcolor: "#fff",
          }}
        />
      </Box>
    </Box>
  );
};

const PdfUploadZone = ({ file, onFile, onRemove, disabled }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const validate = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Only PDF files are accepted");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File is too large — the limit is 20MB");
      return;
    }
    onFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    validate(e.dataTransfer.files?.[0]);
  };

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{
        border: `1.5px dashed ${dragOver ? PALETTE.primary : PALETTE.border}`,
        borderRadius: "16px",
        bgcolor: dragOver ? PALETTE.primarySoft : PALETTE.canvas,
        p: { xs: 3, sm: 5 },
        textAlign: "center",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
      }}
    >
      <AnimatePresence mode="wait">
        {!file ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                bgcolor: PALETTE.surface,
                border: `1px solid ${PALETTE.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 26, color: PALETTE.primary }} />
            </Box>
            <Typography
              sx={{
                fontFamily: FONT.display,
                fontWeight: 600,
                fontSize: 15.5,
                color: PALETTE.ink,
                mb: 0.5,
              }}
            >
              Drop your PDF here, or browse
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT.body,
                fontSize: 13,
                color: PALETTE.inkSoft,
                mb: 2.5,
              }}
            >
              One PDF file, up to 20MB
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={disabled}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                borderColor: PALETTE.border,
                color: PALETTE.ink,
                fontWeight: 600,
                "&:hover": {
                  borderColor: PALETTE.primary,
                  bgcolor: PALETTE.primarySoft,
                },
              }}
            >
              Choose file
              <input
                ref={inputRef}
                type="file"
                hidden
                accept="application/pdf"
                onChange={(e) => validate(e.target.files?.[0])}
              />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="filled"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
              }}
            >
              <CheckCircleIcon sx={{ color: PALETTE.success, fontSize: 22 }} />
              <Typography
                sx={{
                  fontFamily: FONT.display,
                  fontWeight: 600,
                  fontSize: 15,
                  color: PALETTE.ink,
                }}
              >
                Ready to upload
              </Typography>
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: PALETTE.surface,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: "10px",
                px: 1.5,
                py: 0.75,
                mt: 1.75,
                maxWidth: "100%",
              }}
            >
              <PictureAsPdfIcon
                sx={{ fontSize: 18, color: PALETTE.danger, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontFamily: FONT.mono,
                  fontSize: 12.5,
                  color: PALETTE.ink,
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT.mono,
                  fontSize: 11.5,
                  color: PALETTE.inkSoft,
                  flexShrink: 0,
                }}
              >
                · {formatBytes(file.size)}
              </Typography>
              <IconButton
                size="small"
                onClick={onRemove}
                disabled={disabled}
                aria-label="Remove file"
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

const QuestionCard = ({
  q,
  index,
  total,
  answer,
  onAnswerChange,
  onPreview,
}) => {
  const answered = (answer || "").trim().length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.45,
        delay: Math.min(index * 0.06, 0.3),
        ease: "easeOut",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: "16px",
          border: `1px solid ${PALETTE.border}`,
          overflow: "hidden",
          bgcolor: PALETTE.surface,
        }}
      >
        <Box sx={{ display: "flex" }}>
          {/* Ledger spine: encodes question number + answered state */}
          <Box
            sx={{
              width: 56,
              flexShrink: 0,
              bgcolor: PALETTE.canvas,
              borderRight: `1px solid ${PALETTE.border}`,
              display: "flex",
              justifyContent: "center",
              pt: 3,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: answered ? PALETTE.successSoft : PALETTE.surface,
                border: `1.5px solid ${
                  answered ? PALETTE.success : PALETTE.border
                }`,
                transition: "all 0.25s ease",
              }}
            >
              {answered ? (
                <CheckIcon sx={{ fontSize: 16, color: PALETTE.success }} />
              ) : (
                <Typography
                  sx={{
                    fontFamily: FONT.mono,
                    fontSize: 12,
                    fontWeight: 700,
                    color: PALETTE.inkSoft,
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, p: { xs: 2.5, sm: 3.5 }, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT.mono,
                fontSize: 11.5,
                fontWeight: 600,
                color: PALETTE.primary,
                letterSpacing: "0.06em",
                mb: 0.5,
              }}
            >
              QUESTION {String(index + 1).padStart(2, "0")} OF{" "}
              {String(total).padStart(2, "0")}
            </Typography>

            <Typography
              sx={{
                fontFamily: FONT.display,
                fontWeight: 600,
                fontSize: { xs: 15.5, sm: 16.5 },
                color: PALETTE.ink,
                mb: 2.25,
                lineHeight: 1.55,
              }}
            >
              {q.Question}
            </Typography>

            {q.question_image && (
              <Box sx={{ mb: 2.5 }}>
                <SectionLabel
                  icon={<ImageIcon sx={{ fontSize: 15 }} />}
                  text="Question image"
                />
                <Box
                  component={motion.img}
                  whileHover={{ scale: 1.01 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => onPreview(q.question_image, "Question image")}
                  src={q.question_image}
                  alt="Question"
                  sx={{
                    width: "100%",
                    maxHeight: 320,
                    objectFit: "contain",
                    borderRadius: "12px",
                    border: `1px solid ${PALETTE.border}`,
                    cursor: "zoom-in",
                    bgcolor: PALETTE.canvas,
                    display: "block",
                  }}
                />
              </Box>
            )}

            {q.question_pdf && (
              <PdfPreviewPanel url={q.question_pdf} label="Question document" />
            )}

            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Write your answer here..."
              value={answer || ""}
              onChange={(e) => onAnswerChange(q.id, e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  fontFamily: FONT.body,
                  fontSize: 14.5,
                  bgcolor: PALETTE.canvas,
                  "& fieldset": { borderColor: PALETTE.border },
                  "&:hover fieldset": { borderColor: PALETTE.primary },
                  "&.Mui-focused fieldset": {
                    borderColor: PALETTE.primary,
                    borderWidth: "1.5px",
                  },
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: FONT.mono,
                  fontSize: 11,
                  color: PALETTE.inkSoft,
                }}
              >
                {wordCount(answer)} words
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const StudentAssignmentPage = () => {
  const { subId } = useParams();
  const location = useLocation();

  const [pageLoading, setPageLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [submittingText, setSubmittingText] = useState(false);
  const [submittingPdf, setSubmittingPdf] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [assignmentId, setAssignmentId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [pdfFile, setPdfFile] = useState(null);
  const [preview, setPreview] = useState({ open: false, src: "", label: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();

  // Inject display/body/mono fonts once. Move to index.html if you'd rather not do this at runtime.
  useEffect(() => {
    const id = "student-assignment-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const getToken = () => {
    try {
      return JSON.parse(localStorage.getItem("authToken"));
    } catch {
      return null;
    }
  };

  const fetchQuestions = useCallback(async (aId, token) => {
    const res = await fetch(`${API_URL}/assignments/${aId}/questions/`, {
      headers: { Authorization: `Bearer ${token.access}` },
    });
    if (!res.ok) throw new Error("Failed to load questions");
    const data = await res.json();
    setQuestions(Array.isArray(data) ? data : []);
  }, []);

  const fetchAssignment = useCallback(async () => {
    const token = getToken();
    if (!token?.access) {
      setAuthError(true);
      setPageLoading(false);
      return;
    }
    try {
      setPageLoading(true);
      const res = await fetch(`${API_URL}/assignment-mark/${subId}/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      });
      if (!res.ok) throw new Error("Failed to load assignment");
      const data = await res.json();

      const list = data?.assignments;
      if (list?.length > 0) {
        const latest = list[list.length - 1];
        setAssignmentId(latest.id);
        await fetchQuestions(latest.id, token);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load assignment");
    } finally {
      setPageLoading(false);
    }
  }, [subId, fetchQuestions]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  const handleAnswerChange = (id, value) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const answeredCount = useMemo(
    () =>
      questions.filter((q) => (answers[q.id] || "").trim().length > 0).length,
    [questions, answers]
  );

const submitTextAnswers = async () => {
  const token = getToken();

  if (!token?.access) {
    setAuthError(true);
    toast.error("Authentication required");
    return;
  }

  try {
    setSubmittingText(true);

    const studCode = localStorage.getItem("student_code") || "";

    const res = await fetch(`${API_URL}/submit-assignment/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token.access}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        studCode,
        subject_id: Number(subId),
        assignment_id: assignmentId,
        assignment_answers: answers,
      }),
    });

    const data = await res.json();

    console.log(data);

    if (res.ok) {
      toast.success(
        `${data.message} Score: ${data.assignment_score}/${data.total_assignment_marks}`
      );
    } else {
      toast.error(data?.detail || "Submission failed");
    }
  } catch (err) {
    console.error(err);
    toast.error("Network error. Please try again.");
  } finally {
    setSubmittingText(false);
  }
};

  const handleSubmitClick = () => {
    if (answeredCount < questions.length) {
      setConfirmOpen(true);
    } else {
      submitTextAnswers();
    }
  };

  const submitPdf = async () => {
    if (!pdfFile) {
      toast.error("Please choose a PDF file first");
      return;
    }

    const token = getToken();

    try {
      setSubmittingPdf(true);

      const studCode = localStorage.getItem("student_code") || "";

      const formData = new FormData();

      formData.append("studCode", studCode);
      formData.append("subject_id", String(subId));
      formData.append("assignment_id", String(assignmentId));

      // change field name if backend expects pdf_file
      formData.append("pdf_file", pdfFile);

      console.log([...formData.entries()]);

      const res = await fetch(`${API_URL}/submit-assignment-pdf/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access}`,
        },
        body: formData,
      });

      const data = await res.json();

      console.log(data);

      if (res.ok) {
        toast.success(data.message);
        setPdfFile(null);
      } else {
        toast.error(data.detail || data.message || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error");
    } finally {
      setSubmittingPdf(false);
    }
  };

  const openPreview = (src, label) => setPreview({ open: true, src, label });
  const closePreview = () => setPreview((p) => ({ ...p, open: false }));

  // ---- render branches -----------------------------------------------------
  let content;

  if (authError) {
    content = (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: "16px",
            border: `1px solid ${PALETTE.border}`,
          }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: 36, color: PALETTE.danger, mb: 1.5 }}
          />
          <Typography
            sx={{
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 18,
              color: PALETTE.ink,
              mb: 1,
            }}
          >
            Your session has expired
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT.body,
              fontSize: 14,
              color: PALETTE.inkSoft,
              mb: 3,
            }}
          >
            Please log in again to view this assignment.
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: PALETTE.primary,
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { bgcolor: PALETTE.primaryDark, boxShadow: "none" },
            }}
          >
            Back to login
          </Button>
        </Paper>
      </motion.div>
    );
  } else if (pageLoading) {
    content = (
      <Box>
        {[0, 1].map((i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              p: 3.5,
              mb: 3,
              borderRadius: "16px",
              border: `1px solid ${PALETTE.border}`,
            }}
          >
            <Skeleton variant="text" width={140} height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={26} sx={{ mb: 2 }} />
            <Skeleton
              variant="rounded"
              height={110}
              sx={{ mb: 2, borderRadius: "12px" }}
            />
            <Skeleton
              variant="rounded"
              height={84}
              sx={{ borderRadius: "12px" }}
            />
          </Paper>
        ))}
      </Box>
    );
  } else if (questions.length === 0) {
    content = (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "16px",
            border: `1px dashed ${PALETTE.border}`,
            bgcolor: PALETTE.surface,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "16px",
              bgcolor: PALETTE.primarySoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <AssignmentOutlinedIcon
              sx={{ fontSize: 28, color: PALETTE.primary }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 18,
              color: PALETTE.ink,
              mb: 1,
            }}
          >
            Nothing assigned yet
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT.body,
              fontSize: 14,
              color: PALETTE.inkSoft,
              maxWidth: 340,
              mx: "auto",
            }}
          >
            Once your teacher publishes an assignment for this subject, it will
            show up here.
          </Typography>
        </Paper>
      </motion.div>
    );
  } else {
    content = (
      <>
        {questions.map((q, i) => (
          <QuestionCard
            key={q.id}
            q={q}
            index={i}
            total={questions.length}
            answer={answers[q.id]}
            onAnswerChange={handleAnswerChange}
            onPreview={openPreview}
          />
        ))}

        <MotionButton
          fullWidth
          variant="contained"
          onClick={handleSubmitClick}
          disabled={submittingText}
          whileHover={{ scale: submittingText ? 1 : 1.01 }}
          whileTap={{ scale: 0.98 }}
          sx={{
            mb: 5,
            py: 1.6,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 15,
            fontFamily: FONT.body,
            bgcolor: PALETTE.primary,
            boxShadow: "none",
            "&:hover": { bgcolor: PALETTE.primaryDark, boxShadow: "none" },
          }}
        >
          {submittingText ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            `Submit text answers · ${answeredCount}/${questions.length}`
          )}
        </MotionButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 5 }}>
          <Box sx={{ flex: 1, height: "1px", bgcolor: PALETTE.border }} />
          <Typography
            sx={{
              fontFamily: FONT.mono,
              fontSize: 11.5,
              fontWeight: 600,
              color: PALETTE.inkSoft,
              letterSpacing: "0.08em",
            }}
          >
            OR SUBMIT AS A DOCUMENT
          </Typography>
          <Box sx={{ flex: 1, height: "1px", bgcolor: PALETTE.border }} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: "18px",
            border: `1px solid ${PALETTE.border}`,
            bgcolor: PALETTE.surface,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT.display,
              fontWeight: 700,
              fontSize: 17,
              color: PALETTE.ink,
              mb: 0.5,
            }}
          >
            Upload a completed PDF
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT.body,
              fontSize: 13.5,
              color: PALETTE.inkSoft,
              mb: 3,
            }}
          >
            Prefer working offline? Upload a single PDF with all your answers
            instead.
          </Typography>

          <PdfUploadZone
            file={pdfFile}
            onFile={setPdfFile}
            onRemove={() => setPdfFile(null)}
            disabled={submittingPdf}
          />

          <MotionButton
            fullWidth
            variant="contained"
            onClick={submitPdf}
            disabled={submittingPdf || !pdfFile}
            whileHover={{ scale: submittingPdf || !pdfFile ? 1 : 1.01 }}
            whileTap={{ scale: 0.98 }}
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14.5,
              fontFamily: FONT.body,
              bgcolor: PALETTE.ink,
              boxShadow: "none",
              "&:hover": { bgcolor: "#000", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: PALETTE.border,
                color: PALETTE.inkSoft,
              },
            }}
          >
            {submittingPdf ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Upload & submit PDF"
            )}
          </MotionButton>
        </Paper>
      </>
    );
  }

  return (
    <Box sx={{ bgcolor: PALETTE.canvas, minHeight: "100vh" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "rgba(247,248,252,0.85)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${PALETTE.border}`,
        }}
      >
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: "#475569",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "#fff",
                color: "#0f172a",
                transform: "translateX(-2px)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              },
            }}
          >
            <FaArrowLeft size={15} />
          </IconButton>
        </motion.div>
        <Box
          sx={{
            maxWidth: 880,
            mx: "auto",
            px: { xs: 2.5, sm: 4 },
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 600,
                color: PALETTE.primary,
                letterSpacing: "0.08em",
                mb: 0.25,
              }}
            >
              ASSIGNMENT
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: { xs: 19, sm: 23 },
                color: PALETTE.ink,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {location.state?.subjectName || "Assignment details"}
            </Typography>
          </Box>

          {!pageLoading && !authError && questions.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  textAlign: "right",
                  display: { xs: "none", sm: "block" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT.body,
                    fontSize: 12.5,
                    color: PALETTE.inkSoft,
                  }}
                >
                  Answered
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT.mono,
                    fontSize: 13,
                    fontWeight: 600,
                    color: PALETTE.ink,
                  }}
                >
                  {answeredCount} of {questions.length}
                </Typography>
              </Box>
              <ProgressRing value={answeredCount} total={questions.length} />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 880,
          mx: "auto",
          px: { xs: 2.5, sm: 4 },
          py: { xs: 3, sm: 5 },
        }}
      >
        {content}
      </Box>

      <ImageLightbox
        open={preview.open}
        onClose={closePreview}
        src={preview.src}
        label={preview.label}
      />

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT.display,
            fontWeight: 700,
            fontSize: 17,
            color: PALETTE.ink,
          }}
        >
          Submit with unanswered questions?
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ fontFamily: FONT.body, fontSize: 14, color: PALETTE.inkSoft }}
          >
            You've answered {answeredCount} of {questions.length} questions.
            Unanswered questions will be scored as blank.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: "none",
              color: PALETTE.inkSoft,
              fontWeight: 600,
            }}
          >
            Go back
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              submitTextAnswers();
            }}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: PALETTE.primary,
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { bgcolor: PALETTE.primaryDark, boxShadow: "none" },
            }}
          >
            Submit anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentAssignmentPage;
